import React, { useMemo, useState } from "react";
import { Info } from "lucide-react";
import { PageHeader, Field, NumberInput, Result, FormulaBox, Note } from "@/components/CalcUI";
import BodyDiagram from "@/components/BodyDiagram";
import { fmt, cn } from "@/lib/utils";
import { useLanguage } from "@/i18n/LanguageProvider";

const DROP_FACTORS = [
  { value: 20, labelKey: "baxter.factor.macro" },
  { value: 60, labelKey: "baxter.factor.micro" },
];

// Adult A/P Rule of Nines — every limb & torso split into anterior/posterior.
// Groin has no A/P split (single midline region, 1%).
const ADULT_REGIONS = [
  { key: "head_f",  pct: 4.5, en: "Head — anterior",   id: "Kepala — anterior" },
  { key: "head_b",  pct: 4.5, en: "Head — posterior",  id: "Kepala — posterior" },
  { key: "torso_f", pct: 18,  en: "Torso — anterior",  id: "Torso — anterior" },
  { key: "torso_b", pct: 18,  en: "Torso — posterior", id: "Torso — posterior" },
  { key: "rarm_f",  pct: 4.5, en: "R arm — anterior",  id: "Lengan kanan — anterior" },
  { key: "rarm_b",  pct: 4.5, en: "R arm — posterior", id: "Lengan kanan — posterior" },
  { key: "larm_f",  pct: 4.5, en: "L arm — anterior",  id: "Lengan kiri — anterior" },
  { key: "larm_b",  pct: 4.5, en: "L arm — posterior", id: "Lengan kiri — posterior" },
  { key: "rleg_f",  pct: 9,   en: "R leg — anterior",  id: "Tungkai kanan — anterior" },
  { key: "rleg_b",  pct: 9,   en: "R leg — posterior", id: "Tungkai kanan — posterior" },
  { key: "lleg_f",  pct: 9,   en: "L leg — anterior",  id: "Tungkai kiri — anterior" },
  { key: "lleg_b",  pct: 9,   en: "L leg — posterior", id: "Tungkai kiri — posterior" },
  { key: "groin",   pct: 1,   en: "Groin",             id: "Genitalia" },
];

const EMPTY_SELECTED = Object.fromEntries(ADULT_REGIONS.map((r) => [r.key, false]));

export default function Baxter() {
  const { t, lang } = useLanguage();
  const isID = lang === "id";
  const [weight, setWeight] = useState("");
  const [factor, setFactor] = useState(20);
  const [ageGroup, setAgeGroup] = useState("adult"); // adult | pediatric
  const [manualTbsa, setManualTbsa] = useState("");
  const [selected, setSelected] = useState(EMPTY_SELECTED);
  const [pediatricTbsa, setPediatricTbsa] = useState("");

  const buttonTbsa = ADULT_REGIONS.reduce(
    (sum, r) => (selected[r.key] ? sum + r.pct : sum),
    0
  );

  // Effective TBSA:
  //  Adult: manual input > 0 overrides button sum
  //  Pediatric: dedicated manual input only
  let b;
  if (ageGroup === "adult") {
    const m = parseFloat(manualTbsa);
    b = m > 0 ? m : buttonTbsa;
  } else {
    b = parseFloat(pediatricTbsa) || 0;
  }
  const w = parseFloat(weight);
  const f = factor;

  const calc = useMemo(() => {
    if (!w || !b || w <= 0 || b <= 0) return null;
    const multiplier = ageGroup === "pediatric" ? 3 : 4;
    const total = multiplier * w * b;
    const first8 = total / 2;
    const last16 = total / 2;
    return {
      total,
      first8,
      last16,
      first8MlPerHr: first8 / 8,
      last16MlPerHr: last16 / 16,
      first8GttMin: (first8 / 8 * f) / 60,
      last16GttMin: (last16 / 16 * f) / 60,
      multiplier,
    };
  }, [w, b, f, ageGroup]);

  const toggle = (key) => setSelected((s) => ({ ...s, [key]: !s[key] }));

  return (
    <div data-testid="page-baxter">
      <PageHeader
        title={t("baxter.title")}
        subtitle={t("baxter.subtitle")}
        formula={
          ageGroup === "pediatric"
            ? isID
              ? "Total = 3 mL × BB (kg) × %LPB — 50% pada 8 jam pertama, 50% pada 16 jam berikutnya"
              : "Total = 3 mL × body weight (kg) × %TBSA — 50% in first 8 h, 50% in next 16 h"
            : t("baxter.formula")
        }
      />

      <div className="px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl">
        <div className="space-y-6">
          <Field label={t("baxter.weight")} unit="kg" testid="weight">
            <NumberInput value={weight} onChange={setWeight} placeholder="e.g. 70" min={0} testid="weight-input" />
          </Field>

          <Field label={isID ? "Kelompok usia" : "Age group"} testid="age-group">
            <div className="inline-flex border border-stone-200 rounded-sm overflow-hidden bg-white w-full" role="group" data-testid="age-group-toggle">
              {[
                { v: "adult", en: "Adult", id: "Dewasa" },
                { v: "pediatric", en: "Pediatric / Infant", id: "Anak / Bayi" },
              ].map((opt) => {
                const active = ageGroup === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    data-testid={`age-${opt.v}`}
                    onClick={() => setAgeGroup(opt.v)}
                    aria-pressed={active}
                    className={cn(
                      "flex-1 px-3 text-sm transition-colors duration-100 min-h-[44px] font-medium",
                      active ? "bg-sky-700 text-white" : "bg-white text-stone-700 hover:bg-stone-50"
                    )}
                  >
                    {isID ? opt.id : opt.en}
                  </button>
                );
              })}
            </div>
          </Field>

          {ageGroup === "adult" ? (
            <>
              <Field
                label={isID ? "Aturan Sembilan — pilih area anterior/posterior" : "Rule of Nines — select anterior/posterior areas"}
                testid="rule-of-nines"
                hint={
                  isID
                    ? `Total dari tombol: ${fmt(buttonTbsa, 1)}%. Kolom depan (anterior) dan belakang (posterior) dinilai independen.`
                    : `Selected total: ${fmt(buttonTbsa, 1)}%. Anterior & posterior surfaces are scored independently.`
                }
              >
                <div className="grid grid-cols-2 gap-1.5" data-testid="body-part-grid">
                  {ADULT_REGIONS.map((r) => {
                    const active = !!selected[r.key];
                    return (
                      <button
                        key={r.key}
                        type="button"
                        data-testid={`body-${r.key}`}
                        aria-pressed={active}
                        onClick={() => toggle(r.key)}
                        className={cn(
                          "border-2 rounded-md px-2.5 py-2 min-h-[48px] text-left transition-colors flex items-center justify-between gap-2",
                          active
                            ? "border-sky-700 bg-sky-700 text-white shadow-inner"
                            : "border-stone-300 bg-white text-stone-800 hover:border-sky-700/50 hover:bg-sky-700/5"
                        )}
                      >
                        <span className="text-[12px] font-medium leading-tight">{isID ? r.id : r.en}</span>
                        <span className={cn("text-[10px] font-mono px-1.5 py-0.5 rounded-sm border shrink-0", active ? "border-white/50 bg-white/10" : "border-stone-300 bg-stone-50 text-stone-600")}>
                          {r.pct}%
                        </span>
                      </button>
                    );
                  })}
                </div>
              </Field>

              <Field
                label={isID ? "%LPB manual (opsional — mengganti tombol)" : "Manual %TBSA (optional — overrides buttons)"}
                unit="%"
                testid="tbsa"
                hint={t("baxter.tbsa.hint")}
              >
                <NumberInput
                  value={manualTbsa}
                  onChange={setManualTbsa}
                  placeholder={buttonTbsa ? String(buttonTbsa) : "e.g. 30"}
                  min={0}
                  max={100}
                  testid="tbsa-input"
                />
              </Field>
            </>
          ) : (
            <>
              <div
                className="border-2 border-amber-500 bg-amber-50 rounded-md p-5"
                data-testid="pediatric-info-card"
              >
                <div className="flex items-start gap-3">
                  <Info size={20} strokeWidth={1.75} className="text-amber-700 shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-display text-base font-semibold text-amber-900">
                      {isID ? "Anak & bayi: gunakan Palm Rule atau Lund-Browder" : "Pediatric & infants: use the Palm Rule or Lund-Browder"}
                    </h3>
                    <p className="mt-2 text-sm text-amber-900 leading-relaxed">
                      {isID
                        ? "Proporsi tubuh anak berubah dengan usia (kepala relatif besar, tungkai lebih kecil). Aturan Sembilan dewasa tidak akurat."
                        : "Body proportions change with age (relatively large head, smaller legs). The adult Rule of Nines is not accurate."}
                    </p>
                    <ul className="mt-3 space-y-1.5 text-sm text-amber-900 leading-relaxed">
                      <li className="flex gap-2">
                        <span className="font-semibold shrink-0">Palm Rule ·</span>
                        <span>
                          {isID
                            ? "1 telapak tangan pasien (termasuk jari) ≈ 1 % LPB. Metode paling cepat untuk luka bakar berbentuk tak beraturan."
                            : "1 patient palm (including fingers) ≈ 1 % TBSA. Fastest method for irregular burns."}
                        </span>
                      </li>
                      <li className="flex gap-2">
                        <span className="font-semibold shrink-0">Lund-Browder ·</span>
                        <span>
                          {isID
                            ? "Chart usia-spesifik — paling akurat. Rekomendasi standar untuk kasus dirawat inap / dirujuk."
                            : "Age-specific chart — most accurate. Standard recommendation for inpatient / referred cases."}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <Field
                label={isID ? "%LPB terhitung (Palm Rule / Lund-Browder)" : "Calculated %TBSA (Palm Rule / Lund-Browder)"}
                unit="%"
                testid="ped-tbsa"
                hint={
                  isID
                    ? "Masukkan hasil estimasi dari Palm Rule atau chart Lund-Browder. Formula pediatri gunakan pengali 3 mL/kg/%LPB."
                    : "Enter the estimate from Palm Rule or the Lund-Browder chart. Pediatric formula uses the 3 mL/kg/%TBSA multiplier."
                }
              >
                <NumberInput
                  value={pediatricTbsa}
                  onChange={setPediatricTbsa}
                  placeholder="e.g. 20"
                  min={0}
                  max={100}
                  testid="ped-tbsa-input"
                />
              </Field>
            </>
          )}

          <Field label={t("baxter.factor")} testid="factor">
            <div className="inline-flex border border-stone-200 rounded-sm overflow-hidden bg-white w-full" role="group" data-testid="factor-group">
              {DROP_FACTORS.map((d) => {
                const active = factor === d.value;
                return (
                  <button
                    key={d.value}
                    type="button"
                    data-testid={`factor-${d.value}`}
                    onClick={() => setFactor(d.value)}
                    aria-pressed={active}
                    className={cn(
                      "flex-1 px-3 text-sm transition-colors duration-100 min-h-[44px] font-mono",
                      active ? "bg-sky-700 text-white" : "bg-white text-stone-700 hover:bg-stone-50"
                    )}
                  >
                    {t(d.labelKey)}
                  </button>
                );
              })}
            </div>
          </Field>

          <Note kind="warning">{t("baxter.note")}</Note>
        </div>

        <div className="space-y-4">
          {ageGroup === "adult" && (
            <BodyDiagram selected={selected} onToggle={toggle} />
          )}

          <div className="border border-sky-700/30 bg-white rounded-sm p-3" data-testid="tbsa-summary">
            <div className="flex items-baseline justify-between">
              <span className="text-[11px] tracking-[0.14em] uppercase font-semibold text-sky-700">
                {isID ? "Total %LPB terpakai" : "Effective %TBSA"}
              </span>
              <span className="font-mono text-sm text-stone-800">
                {b > 0 ? `${fmt(b, 1)} %` : "—"}
                <span className="text-[10px] uppercase text-stone-400 ml-2">
                  {ageGroup === "pediatric"
                    ? isID ? "manual (Palm / Lund)" : "manual (Palm / Lund)"
                    : parseFloat(manualTbsa) > 0
                    ? isID ? "manual" : "manual"
                    : isID ? "dari tombol" : "from buttons"}
                </span>
              </span>
            </div>
          </div>

          <Result
            label={t("baxter.result.total")}
            value={calc ? fmt(calc.total, 0) : "—"}
            unit="ml / 24 h"
            big
            testid="result-total"
          />

          <div className="border border-sky-700/30 bg-sky-700/5 rounded-sm p-4 space-y-3" data-testid="block-first8">
            <div className="flex items-baseline justify-between">
              <span className="text-[11px] tracking-[0.08em] uppercase font-semibold text-sky-700">
                {t("baxter.block.first8")}
              </span>
              <span className="font-mono text-xs text-stone-500">
                50% · {calc ? fmt(calc.first8, 0) : "—"} ml
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Result label={t("baxter.rate.ml")} value={calc ? fmt(calc.first8MlPerHr, 1) : "—"} unit="ml / hr" testid="result-first8-ml" />
              <Result label={t("baxter.rate.gtt")} value={calc ? fmt(calc.first8GttMin, 0) : "—"} unit={factor === 60 ? "mgtt / min" : "gtt / min"} testid="result-first8-gtt" />
            </div>
          </div>

          <div className="border border-stone-200 bg-stone-50 rounded-sm p-4 space-y-3" data-testid="block-last16">
            <div className="flex items-baseline justify-between">
              <span className="text-[11px] tracking-[0.08em] uppercase font-semibold text-stone-600">
                {t("baxter.block.last16")}
              </span>
              <span className="font-mono text-xs text-stone-500">
                50% · {calc ? fmt(calc.last16, 0) : "—"} ml
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Result label={t("baxter.rate.ml")} value={calc ? fmt(calc.last16MlPerHr, 1) : "—"} unit="ml / hr" testid="result-last16-ml" />
              <Result label={t("baxter.rate.gtt")} value={calc ? fmt(calc.last16GttMin, 0) : "—"} unit={factor === 60 ? "mgtt / min" : "gtt / min"} testid="result-last16-gtt" />
            </div>
          </div>

          <FormulaBox testid="breakdown-box">
            {calc
              ? `Total = ${calc.multiplier} × ${fmt(w, 1)} × ${fmt(b, 1)} = ${fmt(calc.total, 0)} ml / 24 h
First 8 h  → ${fmt(calc.first8, 0)} ml (50%)  ÷ 8 = ${fmt(calc.first8MlPerHr, 1)} ml/hr  →  ${fmt(calc.first8GttMin, 1)} ${factor === 60 ? "mgtt" : "gtt"}/min @ ${factor} gtt/ml
Last 16 h  → ${fmt(calc.last16, 0)} ml (50%) ÷ 16 = ${fmt(calc.last16MlPerHr, 1)} ml/hr  →  ${fmt(calc.last16GttMin, 1)} ${factor === 60 ? "mgtt" : "gtt"}/min @ ${factor} gtt/ml`
              : t("baxter.empty")}
          </FormulaBox>
        </div>
      </div>
    </div>
  );
}
